import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Blog.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BlogPost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchPost = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_URL}/blogs/${slug}`);
                const data = await res.json();
                if (data.success) {
                    setPost(data.data);
                } else {
                    setError('Post not found.');
                }
            } catch (err) {
                setError('Could not load this post.');
            } finally {
                setLoading(false);
            }
        };
        fetchPost();
    }, [slug]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="blog-loading">
                <div className="blog-spinner" />
                <p>Loading post…</p>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="blog-error">
                <p>{error || 'Post not found.'}</p>
                <button onClick={() => navigate('/blog')} className="btn-load-more" style={{ marginTop: '1rem' }}>
                    ← Back to Blog
                </button>
            </div>
        );
    }

    const metaTitle = post.seo?.metaTitle || `${post.title} | Ringez Blog`;
    const metaDesc = post.seo?.metaDescription || post.excerpt;
    const canonical = post.seo?.canonicalUrl || `https://ringez.com/blog/${post.slug}`;

    return (
        <div className="blog-post-page">
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDesc} />
                {post.seo?.focusKeyword && <meta name="keywords" content={post.seo.focusKeyword} />}
                <link rel="canonical" href={canonical} />
                {/* Open Graph */}
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDesc} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={canonical} />
                {post.featuredImage && <meta property="og:image" content={post.featuredImage} />}
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDesc} />
                {post.featuredImage && <meta name="twitter:image" content={post.featuredImage} />}
            </Helmet>

            {/* Hero Image */}
            {post.featuredImage && (
                <div className="blog-post-hero">
                    <img src={post.featuredImage} alt={post.title} />
                </div>
            )}

            <div className="blog-post-container">
                {/* Back button */}
                <Link to="/blog" className="blog-post-back">
                    ← Back to Blog
                </Link>

                {/* Header */}
                <div className="blog-post-header">
                    <span className="blog-category-badge" style={{ marginBottom: '0.75rem', display: 'inline-block' }}>
                        {post.category}
                    </span>
                    <h1>{post.title}</h1>
                    <div className="blog-post-meta">
                        <span>✍️ {post.author}</span>
                        <span className="meta-dot">•</span>
                        <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                        {post.readTime > 0 && (
                            <>
                                <span className="meta-dot">•</span>
                                <span>⏱ {post.readTime} min read</span>
                            </>
                        )}
                        {post.views > 0 && (
                            <>
                                <span className="meta-dot">•</span>
                                <span>👁 {post.views} views</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div
                    className="blog-post-content"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                    <div className="blog-post-tags">
                        {post.tags.map((tag, i) => (
                            <span key={i} className="blog-tag">#{tag}</span>
                        ))}
                    </div>
                )}

                {/* Footer nav */}
                <div style={{ marginTop: '2.5rem', textAlign: 'center' }}>
                    <Link to="/blog" className="blog-post-back" style={{ fontSize: '0.95rem' }}>
                        ← See all articles
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default BlogPost;
